import { ImageScaleViewOpts, View } from '@play-co/timestep-core/ui';
import SpinnerLocal from 'src/game/components/shared/SpinnerLocal';
import { delay } from 'src/lib/utils';
import {
  RaceConfig,
  TargetRaceConfig,
  TargetRaceTheme,
  TimedRaceTheme,
} from 'src/replicant/match2/races/races.ruleset';
import { assertNever } from 'src/replicant/utils';
import { getMutex } from 'src/replicant/utils/mutex';

export type TimedRaceThemeConfig = {
  title: {
    en: ImageScaleViewOpts;
    jp: ImageScaleViewOpts;
  };
  intro: {
    background: ImageScaleViewOpts;
  };
  leaderboard: {
    background: ImageScaleViewOpts;
  };
  icon: ImageScaleViewOpts;
  buttonRankFrame: ImageScaleViewOpts;
};

export const timedRacesSkins: Record<TimedRaceTheme, TimedRaceThemeConfig> = {
  sugar: {
    title: {
      en: {
        image: `assets/ui/match2/races/sugar/title_en.png`,
        width: 450,
        height: 150,
      },
      jp: {
        image: `assets/ui/match2/races/sugar/title_jp.png`,
        width: 413,
        height: 150,
      },
    },
    intro: {
      background: {
        image: `assets/ui/match2/races/sugar/intro_banner.png`,
        width: 500,
        height: 500,
      },
    },
    leaderboard: {
      background: {
        image: `assets/ui/match2/races/sugar/leaderboard_banner.png`,
        width: 646,
        height: 323,
      },
    },

    icon: {
      image: `assets/ui/match2/races/sugar/icon.png`,
    },
    buttonRankFrame: {
      image: `assets/ui/match2/races/sugar/rank_frame.png`,
    },
  },

  crown: {
    title: {
      en: {
        image: `assets/ui/match2/races/crown/title_en.png`,
        width: 450,
        height: 150,
      },
      jp: {
        image: `assets/ui/match2/races/crown/title_jp.png`,
        width: 413,
        height: 150,
      },
    },
    intro: {
      background: {
        image: `assets/ui/match2/races/crown/intro_banner.png`,
        width: 500,
        height: 500,
      },
    },
    leaderboard: {
      background: {
        image: `assets/ui/match2/races/crown/leaderboard_banner.png`,
        width: 646,
        height: 323,
      },
    },
    icon: {
      image: `assets/ui/match2/races/crown/icon.png`,
    },
    buttonRankFrame: {
      image: `assets/ui/match2/races/crown/rank_frame.png`,
    },
  },
};

export type TargetRaceThemeConfig = {
  title: {
    en: ImageScaleViewOpts;
    jp: ImageScaleViewOpts;
  };
  intro: {
    background: ImageScaleViewOpts;
  };
  leaderboard: {
    background: ImageScaleViewOpts;
    scrollArea: ImageScaleViewOpts;
    divider: ImageScaleViewOpts;
    racer: ImageScaleViewOpts;
    racerMe: ImageScaleViewOpts;
    playerFrame: ImageScaleViewOpts;
    playerFrameMe: ImageScaleViewOpts;
    scoreFrame: ImageScaleViewOpts;
  };
  buttonRankFrame: ImageScaleViewOpts;
};

export const targetRaceTheme: Record<TargetRaceTheme, TargetRaceThemeConfig> = {
  turtle: {
    title: {
      en: {
        image: `assets/ui/match2/races/turtle/title_en.png`,
        width: 450,
        height: 89,
      },
      jp: {
        image: `assets/ui/match2/races/turtle/title_jp.png`,
        width: 413,
        height: 108,
      },
    },
    intro: {
      background: {
        image: `assets/ui/match2/races/turtle/intro_banner.png`,
        width: 500,
        height: 500,
      },
    },
    leaderboard: {
      background: {
        image: `assets/ui/match2/races/turtle/leaderboard_banner.png`,
        width: 646,
        height: 323,
      },
      scrollArea: {
        image: `assets/ui/match2/races/turtle/leaderboard_body.png`,
      },
      divider: {
        image: `assets/ui/match2/races/turtle/divider.png`,
      },
      racer: {
        image: `assets/ui/match2/races/turtle/racer.png`,
        width: 107,
        height: 77,
      },
      racerMe: {
        image: `assets/ui/match2/races/turtle/racer_me.png`,
        width: 112,
        height: 76,
      },
      playerFrame: {
        image: `assets/ui/match2/races/turtle/player_frame.png`,
        width: 200,
        height: 72,
        scaleMethod: '3slice',
        sourceSlices: {
          horizontal: { left: 43, right: 43 },
        },
      },
      playerFrameMe: {
        image: `assets/ui/match2/races/turtle/player_frame_me.png`,
        width: 200,
        height: 72,
        scaleMethod: '3slice',
        sourceSlices: {
          horizontal: { left: 43, right: 43 },
        },
      },
      scoreFrame: {
        image: `assets/ui/match2/races/turtle/score_frame.png`,
        width: 40,
        height: 60,
      },
    },
    buttonRankFrame: {
      image: `assets/ui/match2/races/turtle/rank_frame.png`,
    },
  },
};

export type RaceTheme = TimedRaceThemeConfig | TargetRaceThemeConfig;

export function getRaceTheme<T extends RaceConfig>(
  config: T,
): T extends TargetRaceConfig ? TargetRaceThemeConfig : TimedRaceThemeConfig {
  switch (config.type) {
    case 'time':
      return timedRacesSkins[config.theme] as any;
    case 'target':
      return targetRaceTheme[config.theme] as any;
    default:
      assertNever(config);
  }
}

// Use mutex for storing whether the fake race load spinner has already been shown
// ie. locked = not yet shown
let mutex = getMutex();
mutex.lock();

export async function showFakeRaceLoadSpinner(superview: View, color?: string) {
  const spinner = new SpinnerLocal({ superview, color });
  spinner.updateOpts({
    x: 40,
    y: 55,
    scale: 0.85,
  });
  spinner.start();
  await delay(mutex.isLocked() ? 750 : 200);
  spinner.reset();
  spinner.removeFromSuperview();
  mutex.release();
}
